import { EventsRepository } from '@lib/api/repository/events/events.repository';
import AppError from '@lib/app-error';
import {
  AssignEventOrganizerInput,
  EventBannerInput,
  EventFilters,
  EventInput,
  OrganizerCandidatePage,
  OrganizerSearch,
  TransitionEventInput,
  UpdateEventInput,
} from '@lib/types/event';

import { Requester } from '../clients';
import { EventDTO, EventPageDTO, createEvent, eventRequest } from './models';

const PUBLIC_PATH = '/marketplace/events';
const ADMIN_PATH = '/marketplace/admin/events';

export default class EventsAPI implements EventsRepository {
  async list(filters: EventFilters, admin: boolean, signal?: AbortSignal) {
    const { data } = await Requester.get<EventPageDTO>(
      admin ? ADMIN_PATH : PUBLIC_PATH,
      { params: { ...filters }, signal },
    );

    return {
      items: data.items.map(createEvent),
      total: data.total,
      page: data.page,
      limit: data.limit,
    };
  }

  async find(
    identifier: string,
    admin: boolean,
    signal?: AbortSignal,
    viewer?: boolean,
  ) {
    const { data } = await Requester.get<EventDTO>(
      `${admin ? ADMIN_PATH : PUBLIC_PATH}/${encodeURIComponent(identifier)}${viewer && !admin ? '/view' : ''}`,
      { signal },
    );

    return createEvent(data);
  }

  async findEditable(id: string, signal?: AbortSignal) {
    const { data } = await Requester.get<EventDTO>(
      `${PUBLIC_PATH}/${encodeURIComponent(id)}/edit`,
      { signal },
    );

    return createEvent(data);
  }

  async searchOrganizers(filters: OrganizerSearch, signal?: AbortSignal) {
    const { data } = await Requester.get<OrganizerCandidatePage>(
      `${ADMIN_PATH}/organizer-candidates`,
      { params: { ...filters }, signal },
    );

    return {
      items: data.items.map((candidate) => ({
        id: candidate.id,
        name: candidate.name,
        email: candidate.email,
      })),
      total: data.total,
      page: data.page,
      limit: data.limit,
    };
  }

  async assignOrganizer({
    id,
    storeId,
    expectedRevision,
  }: AssignEventOrganizerInput) {
    const { data } = await Requester.put<EventDTO>(
      `${ADMIN_PATH}/${encodeURIComponent(id)}/organizer`,
      { body: { storeId, expectedRevision } },
    );

    return createEvent(data);
  }

  async create(input: EventInput) {
    const { data } = await Requester.post<EventDTO>(ADMIN_PATH, {
      body: eventRequest(input),
    });

    return createEvent(data);
  }

  async update({ id, input, expectedRevision, editor }: UpdateEventInput) {
    const { data } = await Requester.patch<EventDTO>(
      `${editor ? PUBLIC_PATH : ADMIN_PATH}/${encodeURIComponent(id)}`,
      { body: { ...eventRequest(input), expectedRevision } },
    );

    return createEvent(data);
  }

  async transition({
    id,
    action,
    expectedRevision,
    editor,
  }: TransitionEventInput) {
    if (editor && action !== 'remove_banner') {
      throw AppError.Forbidden();
    }

    const { data } = await Requester.post<EventDTO>(
      `${editor ? PUBLIC_PATH : ADMIN_PATH}/${encodeURIComponent(id)}/${editor ? 'banner/remove' : 'transitions'}`,
      { body: editor ? { expectedRevision } : { action, expectedRevision } },
    );

    return createEvent(data);
  }

  async uploadBanner({ id, file, editor, expectedRevision }: EventBannerInput) {
    const body = new FormData();
    body.append('file', file);
    if (expectedRevision !== undefined) {
      body.append('expected_revision', String(expectedRevision));
    }

    const { data } = await Requester.post<EventDTO>(
      `${editor ? PUBLIC_PATH : ADMIN_PATH}/${encodeURIComponent(id)}/banner`,
      { body },
    );

    return createEvent(data);
  }
}
